import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useRouter } from "next/router";
import { initializeSearchParams } from "@/search/Langston/SearchPageProvider/SearchPageProvider.utils";
import { useSearchParamsControls } from "@/search/Langston/useSearchParamsControls";

import {
  type ISearchResults,
  useInfiniteSearchQuery,
} from "@/search/Langston/useInfiniteSearchQuery";

import { useTaxonomyConceptsQuery } from "@/search/Langston/useTaxonomyConceptsQuery";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { worksheets } from "@/search/Langston/worksheets";
import { readingSkills } from "@/search/Langston/readingSkills";
import {
  CONTENT_SETTINGS_TOGGLE_KEY,
  SEARCH_RESULTS_PER_PAGE,
  SELECTED_WORKSHEET,
  VIEW_ALL_STANDARDS,
} from "../SearchPage/SearchPage.constants";

export const SearchPageContext = createContext<any>(undefined);

const SearchPageProvider = ({ children }: ISearchPageProvider) => {
  const { query, push, pathname } = useRouter();

  const [searchParams, setSearchParams] = useState(new URLSearchParams());
  const [debouncedParams, setDebouncedParams] = useState<string>(
    searchParams.toString(),
  );

  const [hasUpdatedTaxonomyFilter, setHasUpdatedTaxonomyFilter] =
    useState<boolean>(false);

  const [sendAdminContentSettings, setSendAdminContentSettings] =
    useState<boolean>(false);

  const [viewAllStandards, setViewAllStandards] = useState<boolean>(false);
  const [selectedWorksheet, setSelectedWorksheet] = useState<boolean>(false);

  const [isDisabledFilters, setIsDisabledFilters] = useState<boolean>(false);

  // Will refetch data any time debouncedParams changes.
  const {
    data: searchResponseData,
    fetchNextPage,
    hasNextPage,
    refetch: refetchSearchResults,
    isFetching: isSearchLoading, // both initial data and pagination loads
  } = useInfiniteSearchQuery({
    urlParams: debouncedParams,
  });

  // Computed value that maps the useInfiniteQuery response into a more
  // readable structure, consolidating only the values we need.
  const searchResponse = useMemo(() => {
    if (!searchResponseData || !searchResponseData.pages.length)
      return undefined;

    const firstPageResults = (searchResponseData.pages[0] as ISearchResults)
      ?.results;
    const totalResultCount =
      firstPageResults.length < SEARCH_RESULTS_PER_PAGE ||
      searchResponseData.pages[0]?.total_result_count === 0
        ? firstPageResults.length
        : searchResponseData.pages[0]?.total_result_count;

    return {
      results: searchResponseData?.pages.flatMap((page) => page.results),
      total_result_count: totalResultCount,
    };
  }, [searchResponseData]);

  const searchResults = useMemo(() => {
    if (isDisabledFilters) {
      // When isDisabledFilters is true, it means that the worksheets filter
      // is selected. Since we are hardcoding worksheets and setting them as
      // searchResults when selected, we have to return early here to avoid
      // updating searchResults with the actual API response.
      return worksheets;
    } else {
      return searchResponse?.results || [];
    }
  }, [searchResponse, isDisabledFilters]);

  const { data: taxonomyConcepts, isLoading: isLoadingTaxonomyData } =
    useTaxonomyConceptsQuery();

  const {
    appendSearchParam,
    deleteSearchParamValues,
    deleteSingleParamValue,
    getAllSearchParams,
    getSingleSearchParamValue,
    getSearchParamValues,
    hasSearchParamValue,
    setMaturityLevelParam,
    setSearchParamValue,
    toggleSearchParamValue,
  } = useSearchParamsControls({
    searchParams,
    setSearchParams,
  });

  const handleWorksheets = (isEnabled: boolean) => {
    if (isEnabled) {
      setIsDisabledFilters(true);
    } else {
      setIsDisabledFilters(false);
    }
  };

  // Updates to the searchParams via the custom functions from useSearchParamsControls()
  // will trigger this useEffect, and the search params to be pushed to the URL.
  useEffect(() => {
    setSearchParams(initializeSearchParams(query));
  }, [query]);

  useEffect(() => {
    if (pathname === "/search" && searchParams.toString()) {
      const next = `?${searchParams.toString()}`;

      if (typeof window !== "undefined" && window.location.search === next)
        return;

      void push({
        pathname: "/search",
        search: next,
      });
    }
  }, [searchParams.toString()]);

  const submitSearch = (newParams: Record<string, string | string[]>) => {
    const nextParams = new URLSearchParams();

    Object.entries(newParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => nextParams.append(key, v));
      } else if (value != null && value !== "") {
        nextParams.set(key, value);
      }
    });

    if (!nextParams.has(LangstonParameterSchema.page.key)) {
      nextParams.set(
        LangstonParameterSchema.page.key,
        LangstonParameterSchema.page.default,
      );
    }

    setSearchParams(new URLSearchParams(nextParams));
  };

  // Debounces all changes to searchParams
  useEffect(() => {
    const raw = searchParams.toString();

    const handler = setTimeout(() => {
      setDebouncedParams(raw);
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchParams.toString()]);

  // Handle query params that aren't part of our Langston parameter schema,
  // since they'll get sanitized out of the url after initial load.
  // These external params provide a way to inform the page context of a
  // given state or value.
  // i.e. Linking to the search page with ?admin_content_settings=true will
  // toggle on the content settings view.
  useEffect(() => {
    if (query[CONTENT_SETTINGS_TOGGLE_KEY] === "true") {
      setSendAdminContentSettings(true);
    }

    if (query[VIEW_ALL_STANDARDS] === "true") {
      setViewAllStandards(true);
    }

    if (query[SELECTED_WORKSHEET] === "true") {
      setSelectedWorksheet(true);
    }
  }, [query]);

  return (
    <SearchPageContext.Provider
      value={{
        getters: {
          readingSkills,
          searchResponse,
          searchResults,
          isDisabledFilters,
          hasNextPage,
          hasUpdatedTaxonomyFilter,
          isSearchLoading,
          sendAdminContentSettings,
          taxonomyConcepts,
          isLoadingTaxonomyData,
          searchParams,
          viewAllStandards,
          selectedWorksheet,
        },
        handlers: {
          appendSearchParam,
          deleteSearchParamValues,
          deleteSingleParamValue,
          fetchNextPage,
          getAllSearchParams,
          getSingleSearchParamValue,
          getSearchParamValues,
          handleWorksheets,
          hasSearchParamValue,
          refetchSearchResults,
          setSearchParamValue,
          setMaturityLevelParam,
          setSendAdminContentSettings,
          setHasUpdatedTaxonomyFilter,
          toggleSearchParamValue,
          submitSearch,
        },
      }}
    >
      {children}
    </SearchPageContext.Provider>
  );
};

export const useSearchPageContext = () => {
  const searchPageContext = useContext(SearchPageContext);

  if (searchPageContext === undefined) {
    throw new Error(
      "searchPageContext must be used within a SearchPageProvider.",
    );
  }

  return searchPageContext;
};

interface ISearchPageProvider {
  children: ReactElement;
}
SearchPageProvider.displayName = "SearchPageProvider";
export { SearchPageProvider };
